import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SquareStack, Trash2 } from 'lucide-react';
import { api, ApiError } from '../api/cliente.js';
import { Botao } from '../componentes/ui/Botao.js';
import { Modal } from '../componentes/ui/Modal.js';
import { Pill } from '../componentes/ui/Pill.js';

interface Template {
  id: string;
  nome: string;
  descricao: string | null;
  padrao: string;
  versao: 'V4' | 'V6';
  usos: number;
  exemplo: string | null;
  operadora: { id: string; nome: string } | null;
}

const SUGESTOES = [
  { nome: 'Genérico por octeto', padrao: 'host-{octeto}.rev.consultnetworks.com.br' },
  { nome: 'IP com hífen', padrao: '{ip-hifen}.rev.consultnetworks.com.br' },
  { nome: 'Blocos maiores que /24', padrao: 'h-{octeto3}-{octeto}.rev.consultnetworks.com.br' },
];

export function Templates() {
  const qc = useQueryClient();
  const [novoAberto, setNovoAberto] = useState(false);

  const { data: templates = [], isLoading } = useQuery({
    queryKey: ['templates'],
    queryFn: () => api.get<Template[]>('/templates'),
  });

  const remover = useMutation({
    mutationFn: (id: string) => api.delete(`/templates/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['templates'] }),
  });

  if (isLoading) return <Esqueleto />;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-[13px] text-texto-suave">
          Padrões reutilizáveis na edição em massa de reversos.
        </p>
        <Botao variante="primario" onClick={() => setNovoAberto(true)}>
          Novo template
        </Botao>
      </div>

      {templates.length === 0 ? (
        <div className="rounded-card border border-borda bg-superficie px-6 py-14 text-center">
          <SquareStack size={24} className="mx-auto mb-2 text-texto-mudo" />
          <p className="text-[15px] font-extrabold">Nenhum template cadastrado</p>
          <p className="mx-auto mt-1 max-w-[420px] text-[13px] text-texto-suave">
            Um padrão evita que cada operador invente uma convenção diferente no meio de uma
            edição em massa.
          </p>
          <Botao variante="primario" className="mt-5" onClick={() => setNovoAberto(true)}>
            Criar o primeiro
          </Botao>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {templates.map((t) => (
            <article key={t.id} className="rounded-card border border-borda bg-superficie p-5">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="truncate text-[14.5px] font-extrabold">{t.nome}</p>
                  {t.descricao && (
                    <p className="mt-0.5 text-[12.5px] leading-snug text-texto-suave">
                      {t.descricao}
                    </p>
                  )}
                </div>
                <Pill tom={t.versao === 'V6' ? 'ipv6' : 'info'} mono>
                  {t.versao === 'V6' ? 'IPv6' : 'IPv4'}
                </Pill>
              </div>

              <p className="mt-3 break-all rounded-chip bg-fundo px-3 py-2 font-mono text-[12.5px]">
                {t.padrao}
              </p>

              {t.exemplo && (
                <p className="mt-2 break-all text-[11.5px] text-texto-suave">
                  <span className="font-semibold">exemplo:</span>{' '}
                  <span className="font-mono">{t.exemplo}</span>
                </p>
              )}

              <div className="mt-4 flex items-center justify-between gap-2">
                <span className="text-[12px] text-texto-mudo">
                  {t.operadora ? t.operadora.nome : 'todas as operadoras'} · {t.usos}{' '}
                  {t.usos === 1 ? 'uso' : 'usos'}
                </span>
                <button
                  onClick={() => remover.mutate(t.id)}
                  aria-label={`Remover ${t.nome}`}
                  className="grid size-8 place-items-center rounded-chip text-texto-mudo hover:bg-erro-fundo hover:text-erro"
                >
                  <Trash2 size={15} />
                </button>
              </div>
            </article>
          ))}
        </div>
      )}

      {novoAberto && (
        <ModalNovo
          aoFechar={() => setNovoAberto(false)}
          aoCriar={() => void qc.invalidateQueries({ queryKey: ['templates'] })}
        />
      )}
    </div>
  );
}

function ModalNovo({ aoFechar, aoCriar }: { aoFechar: () => void; aoCriar: () => void }) {
  const [form, setForm] = useState({
    nome: '',
    descricao: '',
    padrao: '',
    versao: 'V4' as 'V4' | 'V6',
  });

  const criar = useMutation({
    mutationFn: () =>
      api.post('/templates', {
        nome: form.nome,
        descricao: form.descricao.trim() || undefined,
        padrao: form.padrao,
        versao: form.versao,
      }),
    onSuccess: () => {
      aoCriar();
      aoFechar();
    },
  });

  return (
    <Modal
      aberto
      aoFechar={aoFechar}
      titulo="Novo template"
      descricao="A máscara é validada contra um endereço de exemplo antes de ser salva."
      rodape={
        <>
          <Botao onClick={aoFechar}>Cancelar</Botao>
          <Botao
            variante="primario"
            onClick={() => criar.mutate()}
            carregando={criar.isPending}
            disabled={form.nome.length < 2 || form.padrao.length < 3}
          >
            Salvar
          </Botao>
        </>
      }
    >
      <div className="space-y-4">
        <label className="block">
          <Rotulo>Nome</Rotulo>
          <input
            value={form.nome}
            onChange={(e) => setForm((f) => ({ ...f, nome: e.target.value }))}
            autoFocus
            placeholder="Padrão residencial"
            className="h-11 w-full rounded-controle border border-borda px-3 text-[14px] focus:border-marca focus:outline-none"
          />
        </label>

        <label className="block">
          <Rotulo>Máscara</Rotulo>
          <input
            value={form.padrao}
            onChange={(e) => setForm((f) => ({ ...f, padrao: e.target.value }))}
            spellCheck={false}
            placeholder="host-{octeto}.rev.consultnetworks.com.br"
            className="h-11 w-full rounded-controle border border-borda px-3 font-mono text-[13px] focus:border-marca focus:outline-none"
          />
        </label>

        <div>
          <Rotulo>Sugestões</Rotulo>
          <div className="flex flex-wrap gap-1.5">
            {SUGESTOES.map((s) => (
              <button
                key={s.padrao}
                type="button"
                onClick={() => setForm((f) => ({ ...f, padrao: s.padrao, nome: f.nome || s.nome }))}
                className="rounded-chip border border-borda bg-fundo px-2.5 py-1 text-[11.5px] font-semibold text-texto-medio hover:border-marca hover:text-marca"
              >
                {s.nome}
              </button>
            ))}
          </div>
        </div>

        <label className="block">
          <Rotulo>Versão</Rotulo>
          <select
            value={form.versao}
            onChange={(e) => setForm((f) => ({ ...f, versao: e.target.value as 'V4' | 'V6' }))}
            className="h-11 w-full rounded-controle border border-borda px-3 text-[13.5px] focus:border-marca focus:outline-none"
          >
            <option value="V4">IPv4</option>
            <option value="V6">IPv6</option>
          </select>
        </label>

        <label className="block">
          <Rotulo>Descrição</Rotulo>
          <input
            value={form.descricao}
            onChange={(e) => setForm((f) => ({ ...f, descricao: e.target.value }))}
            placeholder="Opcional"
            className="h-11 w-full rounded-controle border border-borda px-3 text-[14px] focus:border-marca focus:outline-none"
          />
        </label>

        {criar.error && (
          <p className="rounded-chip bg-erro-fundo px-3 py-2 text-[12.5px] font-semibold text-erro">
            {(criar.error as ApiError).mensagem}
          </p>
        )}
      </div>
    </Modal>
  );
}

function Rotulo({ children }: { children: React.ReactNode }) {
  return (
    <span className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.6px] text-texto-suave">
      {children}
    </span>
  );
}

function Esqueleto() {
  return (
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
      {[0, 1, 2].map((i) => (
        <div key={i} className="h-48 animate-pulse rounded-card border border-borda bg-superficie" />
      ))}
    </div>
  );
}
